import moment from 'moment'
import { useEffect, useState } from 'react'

import USDCoinSvg from 'src/assets/USDBlueCoin.svg'
import { showAlertError } from 'src/components/alertError'
import { useAuth } from 'src/hooks/auth'
import type {
  BeneficiaryDataProps,
  NewPixFormProps,
} from 'src/pages/pixOptions/components/NewPix/NewPixForm'
import { getBalance } from 'src/requests/transfeera/get-balance'
import { currencyFormat } from 'src/utils/currencyFormat'

interface BeneficiaryDataStepProps extends NewPixFormProps {
  beneficiaryData: BeneficiaryDataProps
  setBeneficiaryData: React.Dispatch<React.SetStateAction<BeneficiaryDataProps>>
}

export function BeneficiaryDataStep({
  nextStep,
  beneficiaryData,
  setBeneficiaryData,
}: BeneficiaryDataStepProps) {
  const { account, user } = useAuth()

  const amount = beneficiaryData?.amount || 0
  const editable = amount === 0

  const maskCpfCnpj = (value: string | undefined): string => {
    if (!value) return ''

    const numericValue = value.replace(/\D/g, '')

    switch (value.length) {
      case 11:
        return `***.${numericValue.slice(3, 6)}.${numericValue.slice(6, 9)}-**`
      case 14:
        return `**.${numericValue.slice(2, 5)}.${numericValue.slice(
          5,
          8
        )}/****-**`
      default:
        return value
    }
  }

  const getCurrentDate = () => {
    return moment().format('DD/MM/YYYY')
  }

  const getCurrentTime = () => {
    return moment().format('HH:mm')
  }

  const [_amount, setAmount] = useState('0,00')

  const handleChangeAmount = (value: string) => {
    setAmount(formatCurrency(value))
  }

  const formatCurrency = (value: string): string => {
    const numericValue = value.replace(/\D/g, '')

    const numberValue = parseFloat(numericValue) / 100

    if (isNaN(numberValue))
      return Number(0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

    return numberValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  const parseCurrencyToNumber = (value: string): number => {
    const normalizedValue = value.replace(/\./g, '').replace(',', '.')

    return parseFloat(normalizedValue) || 0
  }

  const handleNext = () => {
    if (editable) {
      const newAmount = parseCurrencyToNumber(_amount)

      if (newAmount === 0) {
        return showAlertError('Digite um valor')
      }

      setBeneficiaryData({ ...beneficiaryData, amount: newAmount })
    }

    nextStep()
  }

  const [balance, setBalance] = useState(0)

  const getBalanceAccount = async () => {
    const balance = await getBalance()
    if (balance?.value) {
      setBalance(balance?.value)
    } else {
      setBalance(0)
    }
  }

  useEffect(() => {
    getBalanceAccount()
  }, [])

  return (
    <div className="flex flex-col gap-[0.625rem] items-center justify-center px-12 py-20 drop-shadow-md rounded">
      <div className="flex items-center justify-between py-2 px-6 bg-[#E0E0E0] rounded-[1.25rem] w-[389px]">
        <div className="flex flex-col items-start justify-center">
          <span className="text-[0.9375rem]">Valor disponível em conta </span>
          <span className="text-[0.9375rem] font-semibold">
            {currencyFormat(balance)}
          </span>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2 justify-center text-[#004A8A]">
        <img src={USDCoinSvg} alt="" />
        {editable ? (
          <div className="flex items-center shadow-md bg-white gap-1 border border-[#A0A0A0] rounded-[10px] py-3 px-5 placeholder:leading-tight">
            <span>R$</span>
            <input
              value={_amount}
              onChange={(e) => handleChangeAmount(e.target.value)}
            />
          </div>
        ) : (
          <p className="font-semibold text-[1.875rem]">
            {currencyFormat(amount)}
          </p>
        )}
      </div>
      <div className="w-[347px] space-y-[10px]">
        <h4>Quem vai receber</h4>
        <div className="flex flex-col gap-4 my-4 py-4">
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Beneficiário</span>
            <span className="text-xs font-semibold">
              {beneficiaryData.name}
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Informações</span>
            <span className="text-xs font-semibold">
              {beneficiaryData.payer_question}
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Horário</span>
            <span className="text-xs font-semibold">{getCurrentTime()}</span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Conta</span>
            <span className="text-xs font-semibold">
              {beneficiaryData.account}
            </span>
          </div>
          <div className="flex w-full items-center justify-between gap-4">
            <span className="text-xs font-light">Data de pagamento</span>
            <span className="text-xs font-semibold">{getCurrentDate()}</span>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={handleNext}
        className="text-[0.9375rem] font-bold bg-[#004A8A] w-[475px] text-white h-[75px] flex items-center justify-center rounded-[10px] transition hover:brightness-75"
      >
        Confirmar e continuar
      </button>
    </div>
  )
}
